import { useState, useRef, useEffect, useCallback } from 'react';
import { chatWithPlant, isRateLimitError } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { usePlants } from '../../hooks/usePlants';
import { appendDiagnosisChat } from '../../services/diagnosis';
import { getEffectiveInterval } from '../../utils/wateringSchedule';
import { ChatSuggestionCard } from './ChatSuggestionCard';
import type { PlantWithId } from '../../types/plant';
import type { ChatMessage, DiagnosisChatContext } from '../../types/chat';

interface ParsedSuggestion {
  type: 'water_interval';
  value: number;
}

function parseSuggestions(text: string): { cleanText: string; suggestions: ParsedSuggestion[] } {
  const regex = /\[SUGGESTION:WATER_INTERVAL:(\d+)\]/g;
  const suggestions: ParsedSuggestion[] = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const days = parseInt(match[1] ?? '0', 10);
    if (days >= 1 && days <= 90) {
      suggestions.push({ type: 'water_interval', value: days });
    }
  }
  const cleanText = text.replace(regex, '').trim();
  return { cleanText, suggestions };
}

const INPUT_MAX_CHARS = 2000;
const MAX_SEND_MESSAGES = 50;

/** Progressive "thinking" copy — HIGH-thinking diagnosis replies can take 5-15s. */
function thinkingLabel(seconds: number): string {
  if (seconds >= 10) return 'Still working on it…';
  if (seconds >= 4) return 'Looking into this…';
  return 'Thinking…';
}

/** Diagnosis-aware starter questions (avoid generic report-duplicating prompts). */
function diagnosisQuestions(diagnosis: DiagnosisChatContext): string[] {
  if (diagnosis.isHealthy) {
    return ['How do I keep it thriving?', 'What early warning signs should I watch for?'];
  }
  const problems = Array.isArray(diagnosis.problems) ? diagnosis.problems : [];
  const top = problems[0] as { problem?: string } | undefined;
  const name = top?.problem;
  const qs = ['Could light or humidity be causing this?', 'How long until it recovers?'];
  if (name) qs.push(`Is ${name.toLowerCase()} contagious to my other plants?`);
  return qs.slice(0, 3);
}

interface PlantChatProps {
  plant: PlantWithId;
  /** Present ⇒ diagnosis-specialist mode (injects the report, persists the thread). */
  diagnosisContext?: DiagnosisChatContext;
  /** Where to persist the conversation (diagnosis mode only). */
  persist?: { plantId: string; diagnosisId: string };
  /** Saved conversation to restore (diagnosis mode). */
  initialMessages?: ChatMessage[];
}

export function PlantChat({ plant, diagnosisContext, persist, initialMessages }: PlantChatProps) {
  const isDiagnosis = !!diagnosisContext;
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages ?? []);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState<ChatMessage[] | null>(null);
  const [thinkingSeconds, setThinkingSeconds] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user, userDoc } = useAuth();
  const { updatePlant } = usePlants();

  const displayName = plant.nickname || plant.commonName;
  const hadRestoredHistory = (initialMessages?.length ?? 0) > 0;

  const effectiveInterval = getEffectiveInterval(
    plant,
    userDoc?.climateZone ?? null,
    userDoc?.hemisphere ?? null,
  );

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, sending]);

  // Progressive "thinking" timer while a reply is in flight.
  useEffect(() => {
    if (!sending) {
      setThinkingSeconds(0);
      return;
    }
    const id = window.setInterval(() => setThinkingSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [sending]);

  // Auto-resize textarea
  const handleInputChange = useCallback((value: string) => {
    if (value.length <= INPUT_MAX_CHARS) {
      setInput(value);
    }
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, []);

  const persistPair = useCallback(
    async (pair: ChatMessage[]) => {
      if (!isDiagnosis || !persist || !user) return;
      const result = await appendDiagnosisChat(user.uid, persist.plantId, persist.diagnosisId, pair);
      setSaveFailed(result.success ? null : pair);
    },
    [isDiagnosis, persist, user],
  );

  const send = useCallback(
    async (rawText: string) => {
      const trimmed = rawText.trim();
      if (!trimmed || sending) return;

      setError(null);
      setSaveFailed(null);
      const userMessage: ChatMessage = { role: 'user', text: trimmed };
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      setSending(true);

      try {
        const response = await chatWithPlant({
          plantContext: {
            commonName: plant.commonName,
            scientificName: plant.scientificName,
            nickname: plant.nickname,
            careInfo: plant.careInfo,
            location: plant.location,
            lastWatered: plant.lastWatered?.toDate().toISOString() ?? null,
            isCustom: plant.isCustom,
            wateringIntervalDays: plant.wateringIntervalDays ?? null,
            wateringAdjustmentDays: plant.wateringAdjustmentDays ?? 0,
            autoAdjust: plant.autoAdjust ?? true,
            effectiveInterval,
          },
          diagnosisContext,
          // Send-path cap: never exceed the server's 50-message limit.
          messages: updatedMessages.slice(-MAX_SEND_MESSAGES),
        });

        const replyMessage: ChatMessage = { role: 'assistant', text: response.reply };
        setMessages((prev) => [...prev, replyMessage]);
        await persistPair([userMessage, replyMessage]);
      } catch (err) {
        // Roll back the optimistic user message and let them retry.
        setMessages((prev) => prev.filter((m) => m !== userMessage));
        setInput(trimmed);
        if (isRateLimitError(err)) {
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : 'Something went wrong.');
        }
      } finally {
        setSending(false);
      }
    },
    [sending, messages, plant, effectiveInterval, diagnosisContext, persistPair],
  );

  const handleSend = useCallback(() => send(input), [send, input]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRetrySave = useCallback(() => {
    if (saveFailed) persistPair(saveFailed);
  }, [saveFailed, persistPair]);

  const handleAcceptSuggestion = useCallback(
    async (suggestion: ParsedSuggestion) => {
      if (suggestion.type === 'water_interval') {
        await updatePlant(plant.id, {
          wateringIntervalDays: suggestion.value,
          wateringAdjustmentDays: 0,
        });
      }
    },
    [plant.id, updatePlant],
  );

  const starterQuestions = isDiagnosis && diagnosisContext ? diagnosisQuestions(diagnosisContext) : [];

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Message list */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-3 space-y-3"
        role="log"
        aria-live="polite"
        aria-label={`Chat about ${displayName}`}
      >
        {hadRestoredHistory && (
          <div className="flex items-center gap-2 py-1" aria-hidden="true">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-[10px] font-sans uppercase tracking-widest text-gray-400">
              Resumed conversation
            </span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        )}

        {messages.length === 0 && !sending && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
            <span
              className="material-symbols-outlined text-primary/30 mb-3"
              style={{ fontSize: 48 }}
              aria-hidden="true"
            >
              {isDiagnosis ? 'eco' : 'forum'}
            </span>
            <p className="text-gray-400 font-serif text-sm mb-4">
              {isDiagnosis
                ? `Ask the ${plant.commonName} specialist about this diagnosis`
                : `Ask me anything about your ${displayName}`}
            </p>
            {starterQuestions.length > 0 && (
              <div className="flex flex-col gap-2 w-full max-w-xs">
                {starterQuestions.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    className="text-left text-sm font-sans text-editorial-black bg-primary/5 border border-primary/20 rounded-xl px-3 py-2.5 hover:bg-primary/10 transition-colors min-h-[44px]"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => {
          const isAssistant = msg.role === 'assistant';
          const { cleanText, suggestions } = isAssistant && !isDiagnosis
            ? parseSuggestions(msg.text)
            : { cleanText: msg.text, suggestions: [] };

          return (
            <div key={i}>
              <div className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}>
                <div
                  className={`max-w-[80%] px-4 py-2.5 text-sm font-sans whitespace-pre-wrap ${
                    isAssistant
                      ? 'bg-gray-100 text-editorial-black rounded-2xl rounded-bl-md'
                      : 'bg-primary/15 text-editorial-black rounded-2xl rounded-br-md'
                  }`}
                >
                  {cleanText}
                </div>
              </div>
              {suggestions.map((suggestion, si) => (
                <ChatSuggestionCard
                  key={si}
                  type={suggestion.type}
                  value={suggestion.value}
                  onAccept={() => handleAcceptSuggestion(suggestion)}
                />
              ))}
            </div>
          );
        })}

        {/* Typing indicator */}
        {sending && (
          <div className="flex items-center gap-2 justify-start" role="status" aria-label="Thinking">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            {isDiagnosis && thinkingSeconds >= 4 && (
              <span className="text-xs font-sans text-gray-400">{thinkingLabel(thinkingSeconds)}</span>
            )}
          </div>
        )}

        <div ref={scrollRef} />
      </div>

      {/* Save-failed banner (diagnosis mode) */}
      {saveFailed && (
        <button
          type="button"
          onClick={handleRetrySave}
          className="px-3 py-2 bg-warning/10 border-t border-warning/20 text-left w-full"
        >
          <p className="text-xs text-warning font-sans">Couldn&rsquo;t save this conversation. Tap to retry.</p>
        </button>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-3 py-2 bg-danger/10 border-t border-danger/20">
          <p className="text-xs text-danger font-sans">{error}</p>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 px-3 py-2 flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask about ${displayName}...`}
          rows={1}
          maxLength={INPUT_MAX_CHARS}
          disabled={sending}
          className="flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-sans text-editorial-black placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 disabled:opacity-50 min-h-[44px]"
          aria-label={`Message about ${displayName}`}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="flex items-center justify-center w-11 h-11 rounded-full bg-primary text-white disabled:opacity-30 transition-opacity"
          aria-label="Send message"
        >
          <span className="material-symbols-outlined text-xl" aria-hidden="true">send</span>
        </button>
      </div>
    </div>
  );
}
